import React from 'react'
import { useLocation } from 'react-router-dom'

import { AddEmployer } from '@frame/ui-components/add-employer'
import { DirectTaskCard } from '@frame/ui-components/direct-task-card'
import {
  getTaskCardTitle,
  isNotAttempted,
  isStatusFailure,
  isStatusSuccessful,
} from '@frame/ui-components/direct-task-card/utils'

import { DirectTask } from '@frame/lib/api/tasks/types'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { useStore } from '@frame/lib/stores/useStore'
import { addStatusToTasks } from '@frame/lib/utils/tasks'
import { DataAttribute } from '@frame/types'

type Props = {
  tasks: DirectTask[]
  loadingTaskId: string | null
  handleVerify: (task: DirectTask, idx?: number) => void
  handleAddEmployer?: () => void
}

export function TaskList({ tasks, loadingTaskId, handleVerify, handleAddEmployer }: Props) {
  const location = useLocation()
  const { documentsEnabled, credentialsEnabled, irsEnabled, bankEnabled } = useStrategies()
  const { docsForMortgageEnabled } = useStore(({ session }) => ({
    docsForMortgageEnabled: session?.configuration.docs_for_mortgage_enabled ?? false,
  }))

  const tasksWithStatus = addStatusToTasks({
    directTasks: tasks,
    credentialsEnabled,
    documentsEnabled,
    bankEnabled,
    irsEnabled,
    docsForMortgageEnabled,
  })
  const unattemptedTasks = tasksWithStatus.filter((task) => isNotAttempted(task.visualStatus))
  const hasUnattemptedTasks = unattemptedTasks.length > 0
  const hasCompletedTasks = [
    ...tasksWithStatus.filter((task) => isStatusSuccessful(task.visualStatus)),
    ...tasksWithStatus.filter((task) => isStatusFailure(task.visualStatus)),
  ]

  return (
    <div className="mt-5 flex flex-col space-y-3" data-cy={DataAttribute.POS_EMPLOYERS_LIST}>
      {hasCompletedTasks.length > 0 &&
        hasCompletedTasks.map((task) => {
          return (
            <DirectTaskCard
              key={task.id}
              status={task.visualStatus}
              title={getTaskCardTitle(task)}
              onClick={() => handleVerify(task)}
              isLoading={loadingTaskId === task.id}
              animate={location.state?.fromLoginSuccess && location.state?.taskId === task.id}
            />
          )
        })}
      {hasUnattemptedTasks &&
        unattemptedTasks.map((task, idx) => {
          return (
            <DirectTaskCard
              key={task.id}
              status={task.visualStatus}
              title={getTaskCardTitle(task)}
              isNext={idx === 0}
              onClick={() => handleVerify(task, idx)}
              isLoading={loadingTaskId === task.id}
            />
          )
        })}
      {handleAddEmployer && <AddEmployer onClick={handleAddEmployer} isTaskListEmpty={!tasks.length} />}
    </div>
  )
}
